import React, { useRef, useState } from 'react';
import { CommonBackContent } from '../Layouts/CommonBackContent';
import dynamic from 'next/dynamic';
import { InstagramAnonymouslyModel } from '@/types/anonymously.interface';
import { fetchInstagramAnonymouslySearchData } from '@/lib/service/anonymously';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { message } from 'antd';
import { addInsStory } from '@/lib/service/dashboard';
import { AccountLimitAlert, LimitAlert } from '../LandingPage/FeeLimitAlert';
import CommonSimpleModal from '@/components/Common/CommonSimpleModal';
import { EventName } from '@/shared/event-types';
import { ga_logEvent } from '@/shared/ga';
import useMediaNameSync from '@/lib/hooks/useMediaNameSync';
import UserDashboard from '../Dashboard/UserDashboard';
import { InsAccountModel } from '@/types/home';
import useStoryPremium from '@/lib/hooks/isPremium/useStoryPremium';
import TurnstileModal from '@/components/TurnstileModal';
import useTurnstileVerify from '@/lib/hooks/anonymous-instagram-story-viewer/useTurnstileVerify';

const ChooseContent = dynamic(() => import('../LandingPage/ChooseContent'), {
  ssr: true,
});
const FAQContent = dynamic(() => import('./FAQContent'), { ssr: true });
const ChooseViewerForInstagramContent = dynamic(() => import('./ChooseViewerForInstagram'), {
  ssr: true,
});
const TopSearchContent = dynamic(() => import('../LandingPage/TopSearch'), { ssr: true });
// CLS 优化：骨架屏组件
import SearchResultSkeleton from './SearchResultSkeleton';
// CLS 优化：SearchResultContent 使用同步导入，确保骨架屏和内容在同一帧切换
import SearchResultContent from '../LandingPage/SearchResultContent';
const UseViewerForInstagramContent = dynamic(() => import('./UseViewerForInstagram'), {
  ssr: true,
});
const LandingPagePricingContent = dynamic(() => import('../LandingPage/PricingContent'), {
  ssr: true,
});

const WebViewerForInstagramContent = () => {
  const router = useRouter();
  const { media_name } = router.query;

  const [currentModel, setCurrentModel] = useState<InstagramAnonymouslyModel>(null);
  const searchResultRef = useRef<HTMLDivElement>(null);
  const priceRef = useRef<HTMLDivElement>(null);
  const { turnstileRef, getTurnstileToken } = useTurnstileVerify();
  const { t } = useTranslation();

  const chooseContentData = {
    imgPath: '/images/viewer/choose-content-left.jpg',
    parts: [
      {
        imgPath: '/images/viewer/choose-icon-1.webp',
        title: t('View Full Profiles'),
        desc: t(
          'See more than just the latest posts — dive into public Instagram profiles to get the full picture, including bios, follower counts, and more from any public account.'
        ),
      },
      {
        imgPath: '/images/viewer/choose-icon-2.webp',
        title: t('Watch Stories & Highlights'),
        desc: t(
          'Stay updated with temporary content. View active and archived stories, as well as profile highlights, all from public accounts.'
        ),
      },
      {
        imgPath: '/images/viewer/choose-icon-3.webp',
        title: t('Smart Content Search'),
        desc: t(
          "Looking for something specific? Use our search feature to explore any public Instagram account's content quickly, without needing to scroll endlessly."
        ),
      },
    ],
    title: t('Instagram Post Viewer with Stories, Profiles, and Highlights'),
    desc: '',
  };

  const [showLimitPage, setShowLimitPage] = useState<boolean>(false);
  const [showAccountLimit, setShowAccountLimit] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [searchStr, setSearchStr, writeToStorage] = useMediaNameSync(media_name as string, {
    canRead: false,
  });
  const [insListLoading, setInsListLoading] = useState(false);
  const [alertInfo, setAlertInfo] = useState<any>(null);
  const [errorCode, setErrorCode] = useState<string | number>(0);
  // CLS 优化：标记是否因错误需要保持骨架屏显示
  const [keepSkeletonOnError, setKeepSkeletonOnError] = useState(false);
  const { isPremium } = useStoryPremium();
  // CLS 优化：控制内容显示时机，确保骨架屏和内容在同一帧切换
  const [showContent, setShowContent] = useState(false);

  const onValueChanged = (e?: string) => {
    setSearchStr(e ?? '');
  };

  const onSearch = async (e?: string) => {
    if (e !== searchStr && !currentModel) {
      setCurrentModel(null);
    }
    setSearchStr(e ?? '');

    // 增加未登录免费试用后，搜索时不再检查登录状态
    if (loading) {
      return;
    }
    const turnstileToken = await getTurnstileToken();
    if (turnstileToken === null) {
      return;
    }
    onSearchClick(e, turnstileToken);
  };

  const onSearchClick = (e?: string, token?: string) => {
    if (e?.length > 0) {
      // 手动写入搜索内容到 SessionStorage
      if (e.includes('/reel/') || e.includes('/reels/')) {
        // CLS 优化：错误时保持骨架屏显示，直到用户关闭弹窗
        setLoading(true);
        setKeepSkeletonOnError(true);
        setAlertInfo({
          title: t('Invalid Instagram Username'),
          desc: t('The username you entered is not valid. Please check the username and try again.'),
          btnTitle: t('TRYAGAIN'),
          type: 'Instagram-Account-Not-Found',
        });
        return;
      }
      writeToStorage(e);

      if (loading) {
        return;
      }
      setLoading(true);
      // CLS 优化：搜索开始时重置 showContent，显示骨架屏预占位
      // 注意：点击后 500ms 内的布局变化不计入 CLS（hadRecentInput）
      setShowContent(false);
      fetchInstagramAnonymouslySearchData(e, token ?? '', null, {
        isPremium,
        bizFuncName: 'stories_viewer',
      })
        .then((res) => {
          if (res?.code === 0) {
            if (res.data.private_mark) {
              let PrivateData: InsAccountModel = {
                is_private: res.data.private_mark ? 1 : 0,
                profile_pic_url: res.data.avatar,
                media_name: res.data.media_name,
              };
              // CLS 优化：私密账户也需要批量更新状态
              setCurrentModel(res?.data);
              setLoading(false);
              setShowContent(true);
              setAlertInfo({
                title: t('OopsPrivateProfileDetected'),
                desc: (
                  <>
                    <div style={{ marginBottom: '24px', textAlign: 'center' }}>
                      {t('ThisInstagramAccountIsPrivate_Desc')}
                    </div>
                    <UserDashboard {...PrivateData} />
                  </>
                ),
                btnTitle: t('TRYAGAIN'),
                btnDesc: (
                  <p style={{ color: 'var(--app-text-30-color)', marginTop: '10px' }}>
                    {t('Note: Only public accounts can be tracked.')}
                  </p>
                ),
              });

              return;
            }

            // CLS 优化核心：doShowContent 将所有状态更新集中在一起
            // 根据 React 18 自动批处理机制，同一同步函数内的多个 setState 只触发一次渲染
            // 这样骨架屏消失和内容出现在同一帧内完成，不会产生 CLS
            const doShowContent = () => {
              setCurrentModel(res?.data);
              setLoading(false);
              setShowContent(true);
            };

            // 先滚动到目标位置，滚动完成后再显示内容
            // 这样即使骨架屏和内容高度有差异，CLS 也发生在视口边缘或外部
            if (searchResultRef.current) {
              const targetTop = searchResultRef.current.offsetTop - 120;
              window.scrollTo({
                top: targetTop,
                behavior: 'smooth',
              });
              // 等待滚动完成后再显示内容
              let scrollTimer: NodeJS.Timeout;
              const handleScrollEnd = () => {
                clearTimeout(scrollTimer);
                scrollTimer = setTimeout(() => {
                  window.removeEventListener('scroll', handleScrollEnd);
                  doShowContent();
                }, 100);
              };
              window.addEventListener('scroll', handleScrollEnd);
              handleScrollEnd(); // 立即检查一次，以防不需要滚动
            } else {
              doShowContent();
            }
          } else if (res?.code === 22001 || res?.code === 22006) {
            setShowLimitPage(true);
            // 错误时重置 loading，保留之前的 showContent 状态
            setLoading(false);
          } else if (res?.code === 22004 || res?.code === 22004) {
            setErrorCode(res?.code);
            setSearchStr('');
            setCurrentModel(null);
            setLoading(false);
          } else if (res?.code === 22002) {
            // CLS 优化：错误时保持骨架屏显示，直到用户关闭弹窗
            setKeepSkeletonOnError(true);
            setAlertInfo({
              title: t('Invalid Instagram Username'),
              desc: t('The username you entered is not valid. Please check the username and try again.'),
              btnTitle: t('TRYAGAIN'),
              type: 'Instagram-Account-Not-Found',
            });
            // 注意：不调用 setLoading(false)，保持骨架屏显示
          } else {
            // CLS 优化：其他错误（如 Too many requests）也保持骨架屏显示，直到用户关闭弹窗
            setKeepSkeletonOnError(true);
            setAlertInfo({
              title: t('Warning'),
              desc: res?.message || t('An error occurred. Please try again later.'),
              btnTitle: t('OK'),
              type: 'API-Error',
            });
            // 注意：不调用 setLoading(false)，保持骨架屏显示
          }
        })
        .catch(() => {
          // CLS 优化：网络异常也保持骨架屏显示，直到用户关闭弹窗
          setKeepSkeletonOnError(true);
          setAlertInfo({
            title: t('Network Error'),
            desc: t('Network connection failed. Please check your internet connection and try again.'),
            btnTitle: t('OK'),
            type: 'Network-Error',
          });
          // 注意：不调用 setLoading(false)，保持骨架屏显示
        });
    } else {
      message.error(t('Please enter the insert link or username'));
    }
  };

  const onAddAccountToTrackClick = (media_name?: string) => {
    if (insListLoading) {
      return;
    }
    setInsListLoading(true);

    addInsStory(media_name)
      ?.then((res) => {
        if (res?.code === 0) {
          message.success(t('Added successfully'));
          router.push('/dashboard?dashboard_type=toolkit');
        } else {
          if (res?.code !== 22008) {
            message.error(res?.message);
          }

          if (res?.code === 22007) {
            setShowLimitPage(true);
          } else if (res?.code === 22008) {
            setShowAccountLimit(true);
          }
        }
      })
      .finally(() => {
        setInsListLoading(false);
      });
  };

  const gotoPlanClick = () => {
    if (priceRef.current) {
      const targetTop = priceRef.current.offsetTop - 60;
      window.scrollTo({
        top: targetTop,
        behavior: 'smooth',
      });
    }
  };

  return (
    <>
      <CommonBackContent
        style={{
          maxWidth: '1000px',
          margin: '0 auto',
          background: 'transparent',
          minHeight: '100vh',
        }}
      >
        <TopSearchContent
          topInfo={{
            title: t('Post Viewer for Instagram'),
            desc: t(
              `An Instagram Post Viewer lets you view posts, stories, and profiles from public accounts with ease. you can view and download public content completely free in just a few clicks. Whether you're looking for a Post Viewer, Story Viewer, or Profile Viewer, our all-in-one tool is ready for you.`
            ),
          }}
          loading={loading}
          searchVal={searchStr}
          btnPlaceholder={t('View')}
          onSearchClick={(e) => {
            onSearch(e);
            // ga_logEvent(EventName.GA_search_now_clicks)
          }}
          onValueChanged={onValueChanged}
          errorCode={errorCode}
          resetErrorCode={() => setErrorCode(0)}
        />

        {/* CLS 优化：loading 时显示骨架屏预占位，数据返回后同帧切换到真实内容 */}
        <div ref={searchResultRef}>
          {(loading || keepSkeletonOnError) && !showContent ? (
            <SearchResultSkeleton />
          ) : showContent && currentModel ? (
            <SearchResultContent
              loading={insListLoading}
              loadMoreNotCheckLogin={true}
              tabFunc="post"
              post_page_token={currentModel?.post_page_token}
              data={currentModel}
              onAddAccountToTrackClick={onAddAccountToTrackClick}
              onPlanClick={() => setShowLimitPage(true)}
            />
          ) : null}
        </div>

        <ChooseContent data={chooseContentData} />
        <ChooseViewerForInstagramContent />
        <UseViewerForInstagramContent />
        <div ref={priceRef} id="price">
          <LandingPagePricingContent func="story" />
        </div>
      </CommonBackContent>
      <FAQContent />

      <LimitAlert
        open={showLimitPage}
        closeAction={(isOk) => {
          setShowLimitPage(false);

          if (isOk) {
            // ga_logEvent(EventName.GA_search_now_limit_notify_clicked)
            setTimeout(() => {
              gotoPlanClick();
            }, 20);
          }
        }}
      />

      <AccountLimitAlert
        open={showAccountLimit}
        closeAction={(isOk) => {
          setShowAccountLimit(false);

          if (isOk) {
            ga_logEvent(EventName.GA_account_limit_reached_notify_clicked);
            setTimeout(() => {
              gotoPlanClick();
            }, 20);
          }
        }}
      />

      <CommonSimpleModal
        open={alertInfo !== null}
        title={alertInfo?.title}
        desc={alertInfo?.desc}
        btnTitle={alertInfo?.btnTitle}
        closeAction={(isOk) => {
          if (isOk && alertInfo?.href) {
            if (alertInfo.type == 'No-Stories-Detected') {
              setAlertInfo(null);
              router.push(
                `/web-viewer-for-instagram?media_name=${alertInfo?.media_name}&avatar=${alertInfo?.avatar}&media_url=${alertInfo?.media_url}#1`
              );
              return;
            }
            router.push(alertInfo?.href);
          }

          // CLS 优化：用户关闭弹窗后，清除错误状态并隐藏骨架屏
          if (keepSkeletonOnError) {
            setKeepSkeletonOnError(false);
            setLoading(false);
          }

          if (isOk) {
            if ('Out-of-Views' === alertInfo?.type) {
            } else {
              setAlertInfo(null);
            }
          } else {
            setAlertInfo(null);
          }
        }}
      />
      <TurnstileModal ref={turnstileRef} />
    </>
  );
};

export default WebViewerForInstagramContent;
